/**
 * CurrencyDropdown - Searchable dropdown for currency selection with custom currency option
 */

import { useEffect, useState } from 'react'
import { FilterableSelect, type FilterableSelectOption } from '@/components/ui/filterable-select'
import {
  fetchCurrencyDefinitions,
  type CurrencyDefinition,
} from '@/services/currencyDefinitionService'
import { showErrorToast, showSuccessToast } from '@/components/NotificationToast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { X } from 'lucide-react'

interface CurrencyDropdownProps {
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
}

export const CurrencyDropdown = ({
  value,
  onChange,
  disabled,
}: CurrencyDropdownProps): JSX.Element => {
  const [currencies, setCurrencies] = useState<CurrencyDefinition[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customCode, setCustomCode] = useState('')

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setIsLoading(true)
        const definitions = await fetchCurrencyDefinitions()
        // Combine common and other currencies, with common first
        setCurrencies([...definitions.common, ...definitions.other])
      } catch (error) {
        console.error('Failed to load currency definitions:', error)
        showErrorToast('Failed to load currency list')
      } finally {
        setIsLoading(false)
      }
    }

    void loadCurrencies()
  }, [])

  const handleCustomCurrencySubmit = () => {
    const code = customCode.trim().toUpperCase()

    if (!code) {
      showErrorToast('Please enter a currency code')
      return
    }

    if (code.length !== 3) {
      showErrorToast('Currency code must be exactly 3 letters')
      return
    }

    if (!/^[A-Z]{3}$/.test(code)) {
      showErrorToast('Currency code must contain only letters')
      return
    }

    onChange(code)
    setShowCustomInput(false)
    setCustomCode('')
    showSuccessToast(`Custom currency ${code} selected`)
  }

  const handleValueChange = (newValue: string) => {
    if (newValue === '__custom') {
      setShowCustomInput(true)
    } else {
      onChange(newValue)
    }
  }

  // Convert currencies to options format
  const options: FilterableSelectOption[] = currencies.map((currency) => ({
    value: currency.code,
    label: `${currency.code} - ${currency.name}`,
    details: currency.symbol || undefined,
    searchText: `${currency.code} ${currency.name} ${currency.symbol || ''}`,
  }))

  // Add custom currency option at the end
  options.push({
    value: '__custom',
    label: '➕ Add custom currency',
    searchText: 'add custom currency new',
  })

  if (showCustomInput) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCustomCurrencySubmit()
              } else if (e.key === 'Escape') {
                setShowCustomInput(false)
                setCustomCode('')
              }
            }}
            placeholder="Enter 3-letter code (e.g., USD)"
            className="h-9 flex-1"
            maxLength={3}
            autoFocus
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCustomCurrencySubmit}
            className="h-9 px-3"
          >
            Add
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowCustomInput(false)
              setCustomCode('')
            }}
            className="h-9 px-3"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-neutral-500">
          Enter a 3-letter ISO currency code
        </p>
      </div>
    )
  }

  return (
    <FilterableSelect
      value={value}
      onValueChange={handleValueChange}
      options={options}
      disabled={disabled}
      loading={isLoading}
      placeholder="Select currency"
      searchPlaceholder="Search currencies..."
      showClearOption={true}
      clearOptionLabel="No value"
      className="max-h-96"
      triggerClassName="flex h-9 w-full items-center justify-between rounded border border-neutral-200 bg-white px-2 text-sm font-semibold text-neutral-900 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
    />
  )
}
